import React, { useMemo } from 'react'
import { useSelector } from 'hooks/use-redux'
import type { EmailCampaignUser } from 'types/campaign'
import type { Column } from 'components/ui/data-table'
import ContactName from 'components/common/contact-name'
import DataTable from 'components/ui/data-table'
import UserListStat from './users-list-stat'
type Props = {
  compact?: boolean
  users: EmailCampaignUser[]
}

/**
 * Users list component for displaying email campaign user engagement statistics
 * Shows a data table with user information and various engagement metrics
 * @param compact - Whether to use compact column widths
 * @param users - Array of email campaign user objects with engagement data
 */
const UsersList = (props: Props): React.ReactNode => {
  const contactsLoading = useSelector(state => state.entities.contacts.loading)
  const MOST_ENGAGED_USERS_COLUMNS: Column[] = useMemo(
    () => [
      {
        name: 'User',
        toSortable: (prop: EmailCampaignUser) => prop.contact?.name,
        render: (prop: EmailCampaignUser) => (
          <ContactName
            contact={prop.contact}
            user={prop.user}
            loading={contactsLoading}
          />
        ),
      },
      {
        id: 'email_opens',
        type: 'numeric',
        width: props.compact ? 'content' : 'small',
        name: (
          <>
            Email
            <br />
            opens
          </>
        ),
      },
      {
        id: 'page_opens',
        type: 'numeric',
        width: props.compact ? 'content' : 'small',
        name: (
          <>
            Page
            <br />
            opens
          </>
        ),
      },
      {
        name: (
          <>
            Track
            <br />
            streams
          </>
        ),
        type: 'numeric',
        width: props.compact ? 'content' : 'small',
        toSortable: (prop: EmailCampaignUser) => prop.streams,
        render: (prop: EmailCampaignUser) => (
          <UserListStat
            user={prop}
            eventList="tracks_events"
            eventProp="streams"
            caption="Track streams"
            value={prop.streams}
          />
        ),
      },
      {
        name: (
          <>
            Track
            <br />
            downloads
          </>
        ),
        type: 'numeric',
        width: props.compact ? 'content' : 'small',
        toSortable: (prop: EmailCampaignUser) => prop.track_downloads,
        render: (prop: EmailCampaignUser) => (
          <UserListStat
            user={prop}
            eventList="tracks_events"
            eventProp="downloads"
            caption="Track downloads"
            value={prop.track_downloads}
          />
        ),
      },
      {
        name: (
          <>
            Playlist
            <br />
            downloads
          </>
        ),
        type: 'numeric',
        width: props.compact ? 'content' : 'small',
        toSortable: (prop: EmailCampaignUser) => prop.playlist_downloads,
        render: (prop: EmailCampaignUser) => (
          <UserListStat
            user={prop}
            eventList="playlists_events"
            eventProp="downloads"
            caption="Playlist downloads"
            value={prop.playlist_downloads}
          />
        ),
      },
    ],
    [props.compact, contactsLoading],
  )
  return (
    <DataTable
      appearance="plain"
      columns={MOST_ENGAGED_USERS_COLUMNS}
      data={props.users}
    />
  )
}

export default UsersList
